// Remembers which history day (expansion panel) is currently open and restores
// it after a page refresh (F5). Backed by sessionStorage, so the state survives
// reloads and navigation within the tab and clears when the tab is closed.
//
// A component using this mixin must:
//   - define a `panelStorageKey` computed (unique per entity; falsy = don't
//     persist),
//   - bind `v-model="openPanel"` on its <v-expansion-panels>,
//   - give each <Trip> a stable `:panel-value="String(item.id)"`,
//   - assign the list backing those panels BEFORE calling restoreOpenPanel()
//     — restoreOpenPanel()'s $nextTick deferral only selects the right panel
//     because that assignment already landed in the same tick, ahead of the
//     render flush; calling it in the other order reintroduces the bug the
//     deferral fixes,
//   - call `this.restoreOpenPanel(ids[, preferred])` once the history has loaded,
//     passing the list of the same String ids.
export default {
  data: () => ({
    openPanel: undefined,
  }),

  watch: {
    openPanel: function () {
      this.persistOpenPanel()
    },
  },

  methods: {
    // Re-open the previously stored panel, but only if that day is still present
    // in the freshly loaded history (it may have aged out of the range). A
    // `preferred` id — e.g. the trip a notification links to — wins when present.
    restoreOpenPanel: function (ids, preferred) {
      var wanted = preferred != null && ids.indexOf(String(preferred)) !== -1
        ? String(preferred)
        : this.readOpenPanel()
      var restored = (wanted != null && ids.indexOf(wanted) !== -1)
        ? wanted
        : undefined

      // Deferred on purpose, and NOT removable: Vuetify emits `group:selected`
      // from a non-immediate watch on the item's selection state, so a panel
      // that is already selected when it mounts never emits it. Callers assign
      // the list in this same tick, so selecting here would mount the panels
      // pre-selected and no <Trip> would ever learn it had been opened (no
      // lazy load, no live refresh). One tick later the panels exist unselected
      // and the assignment becomes a real transition that does emit.
      this.$nextTick(() => {
        this.openPanel = restored
      })
    },

    persistOpenPanel: function () {
      var key = this.panelStorageKey
      if (!key) return

      try {
        if (this.openPanel == null) {
          window.sessionStorage.removeItem(key)
        } else {
          window.sessionStorage.setItem(key, String(this.openPanel))
        }
      } catch (e) {
        // sessionStorage can be unavailable (private mode, disabled storage).
        this.$log.debug(`Could not persist open panel: ${e}`)
      }
    },

    readOpenPanel: function () {
      var key = this.panelStorageKey
      if (!key) return null

      try {
        return window.sessionStorage.getItem(key)
      } catch {
        return null
      }
    },
  },
}
